import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsightsIcon from '@mui/icons-material/Insights';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Box, Button, Container, IconButton, Stack } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import banner2 from '../images/banner_4.jpg';
import banner3 from '../images/banner_5.jpg';
import banner4 from '../images/finance.webp';
import banner5 from '../images/finance2.webp';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';

const slides = [
  {
    image: banner4,
    badge: 'SEBI REGISTERED',
    badgeIcon: VerifiedIcon,
    eyebrow: 'Research Analyst',
    title: ['Smarter trading,', 'backed by ', { accent: 'real research' }],
    desc: 'A SEBI-registered research analyst delivering disciplined, data-driven calls across equity, futures and options.',
  },
  {
    image: banner2,
    badge: 'PREMIUM ADVISORY',
    badgeIcon: WorkspacePremiumIcon,
    eyebrow: 'Top-notch financial services',
    title: ['Platinum strategies for', { accent: 'serious investors' }],
    desc: 'Curated Platinum & Classic plans across equity and derivatives — built around your risk profile and goals.',
  },
  {
    image: banner3,
    badge: 'MARKET INTELLIGENCE',
    badgeIcon: InsightsIcon,
    eyebrow: 'Experts in market analysis',
    title: ['Deep insights.', { accent: 'Clear actions.' }],
    desc: 'Every recommendation comes with entry, target and stop-loss — no guesswork, just structured trades.',
  },
  {
    image: banner5,
    badge: 'TRUSTED BY 5,000+',
    badgeIcon: ShieldIcon,
    eyebrow: 'Investor-first approach',
    title: ['Compliant. Transparent.', { accent: 'Always on your side.' }],
    desc: 'Open tracksheet, dedicated support and a strict compliance framework — so you focus on your portfolio.',
  },
];

const SLIDE_MS = 6000;

const CustomCarousel = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const total = slides.length;

  const goTo = useCallback(
    (i) => {
      const next = (i + total) % total;
      setDirection(next > index || (index === total - 1 && next === 0) ? 1 : -1);
      setIndex(next);
    },
    [index, total]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  /* Auto-advance with pause on hover */
  useEffect(() => {
    if (paused) return undefined;
    const id = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(id);
  }, [index, paused, next]);

  /* Keyboard arrows */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
    touchStartX.current = null;
  };

  const current = slides[index];
  const BadgeIcon = current.badgeIcon;

  const renderTitle = (parts) =>
    parts.map((p, i) =>
      typeof p === 'string' ? (
        <React.Fragment key={i}>{p} </React.Fragment>
      ) : (
        <Box
          component="span"
          key={i}
          sx={{
            background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            whiteSpace: 'nowrap',
          }}
        >
          {p.accent}
        </Box>
      )
    );

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '88vh', sm: '82vh', md: '86vh' },
        minHeight: { xs: 560, md: 600 },
        maxHeight: 820,
        overflow: 'hidden',
        background: NAVY_DEEP,
        color: '#fff',
        userSelect: 'none',
      }}
    >
      {/* Slide background with Ken-Burns zoom */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={index}
          custom={direction}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.04 }}
          transition={{ opacity: { duration: 1 }, scale: { duration: 6, ease: 'linear' } }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${current.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      </AnimatePresence>

      {/* Dark gradient + brand tint overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            linear-gradient(110deg, ${NAVY_DEEP}EE 0%, ${NAVY}CC 45%, rgba(11,36,71,0.55) 75%, rgba(6,26,54,0.55) 100%),
            radial-gradient(900px 600px at 85% 20%, rgba(239,106,29,0.28) 0%, rgba(239,106,29,0) 60%)
          `,
        }}
      />

      {/* Content */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        <Box sx={{ maxWidth: 720, width: '100%' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.5,
                  py: 0.6,
                  mb: 2.5,
                  borderRadius: 5,
                  background: 'rgba(239,106,29,0.14)',
                  border: '1px solid rgba(239,106,29,0.4)',
                  color: ACCENT,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              >
                <BadgeIcon sx={{ fontSize: '0.95rem' }} />
                {current.badge}
              </Box>

              {/* Eyebrow */}
              <Box
                sx={{
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                  color: '#cbd5e1',
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                {current.eyebrow}
              </Box>

              {/* Title */}
              <Box
                component="h1"
                sx={{
                  m: 0,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: -0.5,
                  fontSize: {
                    xs: '2.1rem',
                    sm: '2.7rem',
                    md: '3.4rem',
                    lg: '3.8rem',
                  },
                  mb: 2.5,
                }}
              >
                {renderTitle(current.title)}
              </Box>

              {/* Description */}
              <Box
                sx={{
                  fontSize: { xs: '0.95rem', md: '1.08rem' },
                  color: '#cbd5e1',
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 560,
                }}
              >
                {current.desc}
              </Box>

              {/* CTAs */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 4 }}
              >
                <Button
                  size="large"
                  onClick={() => navigate('/service')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 3.2,
                    py: 1.3,
                    fontSize: '1rem',
                    borderRadius: 2,
                    boxShadow: '0 10px 24px rgba(239,106,29,0.4)',
                    '&:hover': {
                      background: ACCENT_HOVER,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 28px rgba(239,106,29,0.5)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  Our Services
                </Button>
                <Button
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  onClick={() =>
                    window.open('https://wa.me/919510980382', '_blank')
                  }
                  sx={{
                    background: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3.2,
                    py: 1.3,
                    fontSize: '1rem',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(6px)',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.14)',
                      borderColor: 'rgba(255,255,255,0.35)',
                    },
                  }}
                >
                  Talk on WhatsApp
                </Button>
              </Stack>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Container>

      {/* Prev / Next controls */}
      <IconButton
        onClick={prev}
        aria-label="Previous slide"
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 8, md: 24 },
          transform: 'translateY(-50%)',
          color: '#fff',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          backdropFilter: 'blur(6px)',
          width: { xs: 42, md: 52 },
          height: { xs: 42, md: 52 },
          zIndex: 3,
          '&:hover': {
            background: ACCENT,
            borderColor: ACCENT,
            transform: 'translateY(-50%) scale(1.05)',
          },
          transition: 'all 0.25s ease',
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={next}
        aria-label="Next slide"
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 8, md: 24 },
          transform: 'translateY(-50%)',
          color: '#fff',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          backdropFilter: 'blur(6px)',
          width: { xs: 42, md: 52 },
          height: { xs: 42, md: 52 },
          zIndex: 3,
          '&:hover': {
            background: ACCENT,
            borderColor: ACCENT,
            transform: 'translateY(-50%) scale(1.05)',
          },
          transition: 'all 0.25s ease',
        }}
      >
        <ChevronRightIcon />
      </IconButton>

      {/* Bottom dock: slide counter + dots + progress bar */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(6,26,54,0.65) 100%)',
          pt: 6,
          pb: 2,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ color: '#cbd5e1' }}
            >
              <Box
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  color: '#fff',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {String(index + 1).padStart(2, '0')}
              </Box>
              <Box sx={{ width: 32, height: 1, background: 'rgba(255,255,255,0.3)' }} />
              <Box sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
                {String(total).padStart(2, '0')}
              </Box>
            </Stack>

            {/* Dot pagination */}
            <Stack direction="row" spacing={1}>
              {slides.map((_, i) => {
                const active = i === index;
                return (
                  <Box
                    key={i}
                    role="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goTo(i)}
                    sx={{
                      cursor: 'pointer',
                      height: 4,
                      width: active ? 36 : 14,
                      borderRadius: 4,
                      background: active ? ACCENT : 'rgba(255,255,255,0.35)',
                      transition: 'all 0.35s ease',
                      '&:hover': {
                        background: active ? ACCENT : 'rgba(255,255,255,0.6)',
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>

          {/* Auto-advance progress bar */}
          <Box
            sx={{
              mt: 1.5,
              height: 2,
              width: '100%',
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <motion.div
              key={`${index}-${paused ? 'p' : 'r'}`}
              initial={{ width: 0 }}
              animate={{ width: paused ? '100%' : '100%' }}
              transition={{
                duration: paused ? 0 : SLIDE_MS / 1000,
                ease: 'linear',
              }}
              style={{
                height: '100%',
                background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
              }}
            />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CustomCarousel;
